import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, Settings, LayoutDashboard, Briefcase, ChevronDown, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Header = () => {
  const { user, logout, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'company') return '/company/dashboard';
    return '/dashboard';
  };

  const navLinks = [
    { to: '/projects', label: 'Opportunities' },
    { to: '/how-it-works', label: 'How It Works' },
    { to: '/about', label: 'About' },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-xl border-b border-gray-100 transition-all duration-300" data-testid="header">
      <div className="container-custom flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0" data-testid="logo-link">
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl sm:rounded-2xl bg-teal-700 flex items-center justify-center shadow-lg shadow-teal-700/20 group-hover:shadow-teal-700/40 transition-shadow">
            <span className="text-white font-bold text-xs sm:text-sm tracking-tight">act</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-base sm:text-lg leading-tight text-gray-900">Talent Collective</span>
            <span className="text-[8px] sm:text-[10px] font-semibold tracking-wide text-teal-600">ACT GRANTS x THE PRODUCT FOLKS</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-2">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                location.pathname === to
                  ? "bg-teal-50 text-teal-700"
                  : "text-gray-600 hover:bg-gray-50 hover:text-teal-700"
              )}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Desktop User Menu / CTA */}
        <div className="hidden md:flex items-center space-x-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 hover:bg-teal-50 rounded-full"
                  data-testid="user-menu-trigger"
                >
                  <div className="h-8 w-8 rounded-full bg-teal-700 flex items-center justify-center ring-2 ring-white shadow-md">
                    <span className="text-white text-sm font-medium">
                      {profile?.name?.[0] || user.email?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-700">
                    {profile?.name?.split(' ')[0] || user.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-gray-800">{profile?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                  <span className="inline-flex items-center rounded-full border border-transparent bg-teal-100 text-teal-700 px-2 py-0.5 text-xs font-semibold mt-1.5 capitalize">
                    {user.role}
                  </span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(getDashboardLink())} className="rounded-lg cursor-pointer" data-testid="menu-dashboard">
                  <LayoutDashboard className="mr-2 h-4 w-4 text-teal-600" />
                  Dashboard
                </DropdownMenuItem>
                {user.role === 'talent' && (
                  <DropdownMenuItem onClick={() => navigate('/applications')} className="rounded-lg cursor-pointer" data-testid="menu-applications">
                    <Briefcase className="mr-2 h-4 w-4 text-teal-600" />
                    My Applications
                  </DropdownMenuItem>
                )}
                {user.role === 'company' && (
                  <DropdownMenuItem onClick={() => navigate('/company/projects')} className="rounded-lg cursor-pointer" data-testid="menu-projects">
                    <Briefcase className="mr-2 h-4 w-4 text-teal-600" />
                    My Projects
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => navigate(user.role === 'company' ? '/company/settings' : '/settings')}
                  className="rounded-lg cursor-pointer"
                  data-testid="menu-settings"
                >
                  <Settings className="mr-2 h-4 w-4 text-teal-600" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 rounded-lg cursor-pointer" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/signup">
              <Button
                className="text-sm font-semibold bg-teal-700 hover:bg-teal-800 text-white rounded-full px-6 shadow-lg shadow-teal-700/20 hover:shadow-teal-700/30 transition-all duration-300"
                data-testid="signup-btn"
              >
                Get Started
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center space-x-2">
          {user ? (
            <div className="h-8 w-8 rounded-full bg-teal-700 flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {profile?.name?.[0] || user.email?.[0]?.toUpperCase()}
              </span>
            </div>
          ) : null}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <nav className="container-custom py-4 space-y-2">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={handleNavClick}
                className={cn(
                  "block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200",
                  location.pathname === to
                    ? "bg-teal-50 text-teal-700"
                    : "text-gray-700 hover:bg-gray-50"
                )}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {label}
              </Link>
            ))}
            
            <div className="pt-4 mt-2 border-t border-gray-100">
              {user ? (
                <>
                  <div className="px-4 py-2 mb-2">
                    <p className="text-sm font-semibold text-gray-800">{profile?.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    onClick={handleNavClick}
                    className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50"
                    data-testid="mobile-menu-dashboard"
                  >
                    <LayoutDashboard className="mr-3 h-5 w-5 text-teal-600" />
                    Dashboard
                  </Link>
                  {user.role === 'talent' && (
                    <Link
                      to="/applications"
                      onClick={handleNavClick}
                      className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50"
                      data-testid="mobile-menu-applications"
                    >
                      <Briefcase className="mr-3 h-5 w-5 text-teal-600" />
                      My Applications
                    </Link>
                  )}
                  <Link
                    to={user.role === 'company' ? '/company/settings' : '/settings'}
                    onClick={handleNavClick}
                    className="flex items-center px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50"
                    data-testid="mobile-menu-settings"
                  >
                    <Settings className="mr-3 h-5 w-5 text-teal-600" />
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 rounded-xl text-base font-medium text-red-600 hover:bg-red-50"
                    data-testid="mobile-menu-logout"
                  >
                    <LogOut className="mr-3 h-5 w-5" />
                    Log out
                  </button>
                </>
              ) : (
                <Link to="/signup" onClick={handleNavClick}>
                  <Button
                    className="w-full h-12 text-base font-semibold bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
                    data-testid="mobile-signup-btn"
                  >
                    Get Started
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
